import { PaneBackButton } from "@/components/PaneBackButton";
import { PaneExpandButton } from "@/components/PaneExpandButton";
import {
  usePaneLocation,
  usePaneParams,
  usePaneRouterContextSafe,
} from "@/components/PaneRouter";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ROUTES } from "@/constants/routes";
import { useAppNavigate } from "@/hooks/pane-routing";
import {
  CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS,
  CANVAS_DETAIL_PANE_HEADER_CLASS,
} from "@/panes/canvasDetailPaneHeaderClasses";
import { EntityPaneHeaderObjectActions } from "@/panes/shared/EntityPaneHeaderObjectActions";
import { entityWithEnrichedOpenTabUrl } from "@/panes/shared/entityWithEnrichedOpenTabUrl";
import {
  IntegrationIcon,
  Markdown,
  PaneLayout,
  PaneLayoutContent,
  PaneLayoutHeader,
  PaneLayoutHeaderActions,
  UnderlineTabs,
} from "@cohesive/ui-components";
import { format } from "date-fns";
import {
  Clock,
  ExternalLink,
  FileText,
  MapPin,
  StickyNote,
  Users,
  X,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useGranolaConnection } from "./hooks/useGranolaConnection";
import { useGranolaMeetingFetch } from "./hooks/useGranolaMeetingFetch";
import { useGranolaTranscriptFetch } from "./hooks/useGranolaTranscriptFetch";

type GranolaDetailTab = "notes" | "transcript";

function formatTimestamp(ts?: string): string | undefined {
  if (!ts) return undefined;
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? undefined : format(d, "PPpp");
}

function attendeeNames(attendees: unknown): string[] {
  if (!Array.isArray(attendees)) return [];
  return attendees
    .map((a) => {
      const r = (a ?? {}) as { display_name?: string; email?: string };
      return r.display_name || r.email || "";
    })
    .filter(Boolean);
}

/**
 * Granola meeting detail. Renders the generic entity immediately and enriches it
 * with the AI notes + attendees fetched by id via `granola-mcp-get-meetings`.
 */
export function GranolaMeetingDetailPane() {
  const appNavigate = useAppNavigate();
  const paneContext = usePaneRouterContextSafe();
  const paneLocation = usePaneLocation("rightPane");
  const mainLocation = useLocation();
  const { meetingId } = usePaneParams(ROUTES.GRANOLA.MEETING_DETAIL);

  const entity = paneLocation?.state?.entity ?? mainLocation.state?.entity;
  const data = (entity?.data ?? {}) as Record<string, unknown>;
  const id = (data.id as string | undefined) ?? meetingId ?? "";

  const [activeTab, setActiveTab] = useState<GranolaDetailTab>("notes");

  const { connectionId, hasConnection } = useGranolaConnection();
  const {
    data: meeting,
    isLoading,
    error,
  } = useGranolaMeetingFetch({
    integrationConnectionId: connectionId,
    meetingId: id,
    enabled: !!connectionId && !!id,
  });

  // Lazily fetch the transcript only once the user opens its tab — it can be
  // large and most pane views never need it. react-query caches the result so
  // tab-switching doesn't refetch.
  const {
    data: transcript,
    isLoading: isTranscriptLoading,
    error: transcriptError,
  } = useGranolaTranscriptFetch({
    integrationConnectionId: connectionId,
    meetingId: id,
    enabled: !!connectionId && !!id && activeTab === "transcript",
  });

  // Prefer fetched fields, fall back to the generic entity.
  const title =
    (meeting?.title as string | undefined) ??
    (data.title as string | undefined) ??
    "Untitled meeting";
  const when =
    formatTimestamp(meeting?.start_time as string | undefined) ??
    formatTimestamp(data.timestamp as string | undefined);
  const location =
    (meeting?.location as string | undefined) ??
    (data.subtitle as string | undefined);
  const notes =
    (meeting?.enhanced_notes as string | undefined) ??
    (meeting?.body as string | undefined);
  const names = attendeeNames(meeting?.attendees);
  const url =
    (meeting?.granola_url as string | undefined) ??
    (meeting?.web_link as string | undefined) ??
    (data.url as string | undefined);

  const entityForHeaderActions = useMemo(
    () =>
      entity
        ? entityWithEnrichedOpenTabUrl(entity, {
            url: data.url as string | undefined,
            web_link: meeting?.web_link as string | undefined,
            granola_url: meeting?.granola_url as string | undefined,
          })
        : undefined,
    [entity, data.url, meeting?.web_link, meeting?.granola_url],
  );

  const handleClose = useCallback(() => {
    appNavigate({ pane: "rightPane", to: null });
  }, [appNavigate]);

  const handleOpenExternal = useCallback(() => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  }, [url]);

  return (
    <PaneLayout>
      <PaneLayoutHeader className={CANVAS_DETAIL_PANE_HEADER_CLASS}>
        <PaneLayoutHeaderActions
          className={CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS}
        >
          <PaneBackButton />

          <div className="flex min-w-0 flex-1 items-center gap-1.5 pl-1">
            <IntegrationIcon
              platform="granola"
              name="Granola"
              className="h-4 w-4 shrink-0"
            />
            <span className="text-sm font-medium text-foreground truncate leading-none">
              {title}
            </span>
          </div>

          <TooltipProvider>
            {entityForHeaderActions && (
              <EntityPaneHeaderObjectActions
                entity={entityForHeaderActions}
                name={title}
              />
            )}

            <PaneExpandButton />

            {paneContext && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="toolbarGhost"
                    size="icon-sm"
                    className="shrink-0"
                    onClick={handleClose}
                    aria-label="Close"
                  >
                    <X />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Close</TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
        </PaneLayoutHeaderActions>
      </PaneLayoutHeader>

      <PaneLayoutContent className="!p-0">
        <div className="flex flex-col gap-4 p-4">
          <div>
            <h2 className="text-base font-semibold text-foreground leading-snug">
              {title}
            </h2>
            <div className="mt-1.5 flex flex-col gap-1 text-sm text-muted-foreground">
              {when && (
                <span className="flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 shrink-0" />
                  {when}
                </span>
              )}
              {location && (
                <span className="flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5 shrink-0" />
                  {location}
                </span>
              )}
            </div>
          </div>

          {names.length > 0 && (
            <div>
              <p className="mb-1 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <Users className="h-3.5 w-3.5" />
                Attendees
              </p>
              <p className="text-sm text-foreground">{names.join(", ")}</p>
            </div>
          )}

          <div>
            <UnderlineTabs
              tabs={[
                {
                  id: "notes",
                  label: "Notes",
                  icon: <StickyNote className="h-3.5 w-3.5" />,
                },
                {
                  id: "transcript",
                  label: "Transcript",
                  icon: <FileText className="h-3.5 w-3.5" />,
                },
              ]}
              value={activeTab}
              onValueChange={(id) => setActiveTab(id as GranolaDetailTab)}
            />

            <div className="pt-3">
              {activeTab === "notes" ? (
                isLoading && !meeting ? (
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-5/6" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                ) : notes ? (
                  <Markdown className="break-words text-sm text-foreground leading-relaxed [&_p]:my-2 [&_:first-child]:mt-0 [&_:last-child]:mb-0">
                    {notes}
                  </Markdown>
                ) : error && !hasConnection ? (
                  <p className="text-sm text-muted-foreground">
                    Connect Granola to view meeting notes.
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No notes available for this meeting.
                  </p>
                )
              ) : isTranscriptLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-5/6" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              ) : transcript ? (
                <p className="whitespace-pre-wrap break-words text-sm text-foreground leading-relaxed">
                  {transcript}
                </p>
              ) : transcriptError && !hasConnection ? (
                <p className="text-sm text-muted-foreground">
                  Connect Granola to view the transcript.
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No transcript available for this meeting.
                </p>
              )}
            </div>
          </div>

          {url && (
            <div>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={handleOpenExternal}
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Open in Granola
              </Button>
            </div>
          )}
        </div>
      </PaneLayoutContent>
    </PaneLayout>
  );
}
